/**
 * The YouTube embed's message API, as the page sees it: what the player reports, folded into one
 * state, and the one sample the watch page's transport reads from it. YouTube keeps its own chrome
 * — this only lets the rest of the page follow along (the playing chapter, the resume position, the
 * history mirror) and lets a chapter or a timestamp seek the embed.
 *
 * Spoken directly over `postMessage` rather than through YouTube's `iframe_api` script, so the page
 * itself never runs YouTube's code. The protocol is that script's, measured 2026-10-09 against
 * Chrome 155 (`integrations.md`): the page keeps sending `listening`, and once the player hears it
 * the player pushes `initialDelivery` once and then `infoDelivery` deltas — about every 260 ms while
 * playing, each with `currentTime`, and a `playerState` whenever that changes. Commands are
 * `{ event: 'command', func, args }`. No listener registration is needed for any of it.
 */

/** The only origin the player's messages may come from, and the target origin for ours. */
export const YOUTUBE_EMBED_ORIGIN = 'https://www.youtube.com';

/** The `id` on every message, and the embed address's `widgetid` — one embed per page. */
export const YOUTUBE_EMBED_WIDGET_ID = 1;

/** YouTube's own numbering: unstarted, ended, playing, paused, buffering, cued. */
const PLAYING = 1;
const ENDED = 0;
const BUFFERING = 3;

/**
 * How far past the last report the position is carried forward while playing. A report arrives
 * every ~260 ms, so this covers a late one with room to spare without letting a stall nobody
 * reported run the playhead away.
 */
const MAX_EXTRAPOLATION_SEC = 1;

export interface YoutubeEmbedState {
  readonly videoId: string | null;
  /** `null` until the first report carrying a position — before that there is nothing to sample. */
  readonly currentTimeSec: number | null;
  /** When {@link currentTimeSec} was reported, on the caller's clock. */
  readonly reportedAtMs: number;
  readonly durationSec: number;
  readonly loadedFraction: number;
  readonly playerState: number;
  readonly muted: boolean;
  /** 0–1, the page's scale rather than YouTube's 0–100. */
  readonly volume: number;
  readonly playbackRate: number;
}

export const INITIAL_YOUTUBE_EMBED_STATE: YoutubeEmbedState = {
  videoId: null,
  currentTimeSec: null,
  reportedAtMs: 0,
  durationSec: 0,
  loadedFraction: 0,
  playerState: -1,
  muted: false,
  volume: 1,
  playbackRate: 1,
};

/** The transport's own sample shape, so the watch page reads the embed exactly like its player. */
export interface YoutubeEmbedSample {
  readonly currentTimeSec: number;
  readonly durationSec: number;
  readonly bufferedEndSec: number;
  readonly paused: boolean;
  readonly ended: boolean;
  readonly muted: boolean;
  readonly volume: number;
  readonly playbackRate: number;
}

type Info = Readonly<Record<string, unknown>>;

/**
 * The state-bearing part of one message, or `null` for anything else — a message from some other
 * widget, junk, or an event this page has no use for. Total: the input is whatever arrived on
 * `window`'s message channel. `onStateChange` is folded in as a `playerState` delta, though the
 * player only sends it to a page that registered for it.
 */
export function parseYoutubeEmbedMessage(data: unknown): Info | null {
  const message = messageOf(data);
  if (message === null) return null;
  const { event, info } = message;
  if (event === 'onStateChange' && typeof info === 'number') return { playerState: info };
  if (event !== 'infoDelivery' && event !== 'initialDelivery') return null;
  if (typeof info !== 'object' || info === null || Array.isArray(info)) return null;
  return info as Info;
}

/**
 * Whether a message is an embed document's first report. The player sends `initialDelivery` once per
 * document, so one arriving after the page already had a position means the frame was reloaded and
 * the new document started over from the address's `start` — which happens *before* the frame's
 * `load` event, since the new player answers the running handshake as soon as its script is up.
 */
export function isYoutubeEmbedInitialDelivery(data: unknown): boolean {
  return messageOf(data)?.['event'] === 'initialDelivery';
}

function messageOf(data: unknown): Readonly<Record<string, unknown>> | null {
  let message: unknown = data;
  if (typeof data === 'string') {
    try {
      message = JSON.parse(data);
    } catch {
      return null;
    }
  }
  return typeof message === 'object' && message !== null ? (message as Readonly<Record<string, unknown>>) : null;
}

function finite(value: unknown): number | null {
  return typeof value === 'number' && Number.isFinite(value) ? value : null;
}

/** Fold one message's info into the state. Fields the message does not carry keep their value. */
export function mergeYoutubeEmbedInfo(state: YoutubeEmbedState, info: Info, nowMs: number): YoutubeEmbedState {
  const currentTime = finite(info['currentTime']);
  const duration = finite(info['duration']);
  const loaded = finite(info['videoLoadedFraction']);
  const playerState = finite(info['playerState']);
  const volume = finite(info['volume']);
  const rate = finite(info['playbackRate']);
  const videoData = info['videoData'];
  const videoId =
    typeof videoData === 'object' && videoData !== null && typeof (videoData as Info)['video_id'] === 'string'
      ? ((videoData as Info)['video_id'] as string)
      : null;

  return {
    videoId: videoId === null || videoId === '' ? state.videoId : videoId,
    currentTimeSec: currentTime === null ? state.currentTimeSec : Math.max(0, currentTime),
    reportedAtMs: currentTime === null ? state.reportedAtMs : nowMs,
    durationSec: duration === null || duration < 0 ? state.durationSec : duration,
    loadedFraction: loaded === null ? state.loadedFraction : Math.min(1, Math.max(0, loaded)),
    playerState: playerState === null ? state.playerState : playerState,
    muted: typeof info['muted'] === 'boolean' ? info['muted'] : state.muted,
    volume: volume === null ? state.volume : Math.min(1, Math.max(0, volume / 100)),
    playbackRate: rate === null || rate <= 0 ? state.playbackRate : rate,
  };
}

/**
 * A seek the page just asked for, written ahead of the player's own report so the transport agrees
 * with the click at once — the same reason the watch page re-samples its own player after a seek.
 */
export function seekYoutubeEmbedState(state: YoutubeEmbedState, timeSec: number, nowMs: number): YoutubeEmbedState {
  if (state.currentTimeSec === null || !Number.isFinite(timeSec)) return state;
  return { ...state, currentTimeSec: Math.max(0, timeSec), reportedAtMs: nowMs };
}

/**
 * What the transport should read now, or `null` when there is nothing trustworthy to read.
 *
 * **The reports carry no ad marker**, so a sample is refused when it plainly describes some other
 * video: another `video_id`, or — when the page knows the video's length — a duration more than
 * 3 s or 2 % away from it. That keeps an ad's clock out of the resume position and the chapter
 * highlight; the page simply holds the last good sample until the video itself is back.
 *
 * While playing, the position is carried forward from the last report by the wall clock (at most
 * {@link MAX_EXTRAPOLATION_SEC}), since the reports come slower than the page polls.
 */
export function youtubeEmbedSample(
  state: YoutubeEmbedState,
  nowMs: number,
  expected: { readonly videoId: string; readonly durationSec: number | null },
): YoutubeEmbedSample | null {
  if (state.currentTimeSec === null) return null;
  if (state.videoId !== null && state.videoId !== expected.videoId) return null;
  const known = expected.durationSec;
  if (known !== null && known > 0 && state.durationSec > 0) {
    if (Math.abs(state.durationSec - known) > Math.max(3, known * 0.02)) return null;
  }

  const playing = state.playerState === PLAYING;
  const elapsedSec = playing ? Math.min(MAX_EXTRAPOLATION_SEC, Math.max(0, (nowMs - state.reportedAtMs) / 1000)) : 0;
  const carried = state.currentTimeSec + elapsedSec * state.playbackRate;
  const current = state.durationSec > 0 ? Math.min(state.durationSec, carried) : carried;

  return {
    currentTimeSec: current,
    durationSec: state.durationSec,
    bufferedEndSec: state.loadedFraction * state.durationSec,
    // Buffering is a playing element waiting for bytes, as it is for `<video>`: not paused.
    paused: !playing && state.playerState !== BUFFERING,
    ended: state.playerState === ENDED,
    muted: state.muted,
    volume: state.volume,
    playbackRate: state.playbackRate,
  };
}

/** The handshake. Sent until the player answers — before its document loads it has no listener. */
export function youtubeEmbedListeningMessage(): string {
  return JSON.stringify({ event: 'listening', id: YOUTUBE_EMBED_WIDGET_ID, channel: 'widget' });
}

export type YoutubeEmbedCommand =
  | { readonly kind: 'play' }
  | { readonly kind: 'pause' }
  | { readonly kind: 'seek'; readonly timeSec: number }
  | { readonly kind: 'mute'; readonly muted: boolean }
  | { readonly kind: 'volume'; readonly level: number };

/** One command, spelled as the player's message API expects it. */
export function youtubeEmbedCommandMessage(command: YoutubeEmbedCommand): string {
  const call = (func: string, args: readonly unknown[] = []): string =>
    JSON.stringify({ event: 'command', func, args, id: YOUTUBE_EMBED_WIDGET_ID, channel: 'widget' });
  switch (command.kind) {
    case 'play':
      return call('playVideo');
    case 'pause':
      return call('pauseVideo');
    case 'seek':
      // `true`: allow a seek past the buffered range, which is every seek a chapter asks for.
      return call('seekTo', [Math.max(0, command.timeSec), true]);
    case 'mute':
      return call(command.muted ? 'mute' : 'unMute');
    case 'volume':
      return call('setVolume', [Math.round(Math.min(1, Math.max(0, command.level)) * 100)]);
  }
}
